import React, { useEffect, useRef, useState } from 'react';
import { useInlineEdit } from '../../context/InlineEditContext';
import { Loader2, Check } from 'lucide-react';

/**
 * EditableText — click-to-edit wrapper for any CMS-backed text on the public site.
 *
 * Usage:
 *   <EditableText as="h1" scope={{ type: 'homepage_section', sectionId, path: 'headline' }}
 *                 value={data.headline} className="text-4xl">
 *     {data.headline}
 *   </EditableText>
 *
 * When not in edit mode → renders children as the underlying tag (default 'span').
 * When admin toggles Edit Mode ON → hover shows dashed outline; click makes it contentEditable;
 * blur auto-saves via the shared save router in InlineEditContext.
 * Enter inserts a real newline (multi-line by default). Escape cancels the edit.
 */
export const EditableText = ({
  as: Tag = 'span',
  scope,
  value,
  className = '',
  placeholder = 'Click to edit',
  children,
  multiline = true,
}) => {
  const { enabled, saveField } = useInlineEdit();
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const initialRef = useRef(value ?? (typeof children === 'string' ? children : ''));

  useEffect(() => {
    initialRef.current = value ?? (typeof children === 'string' ? children : '');
  }, [value, children]);

  if (!enabled) {
    return <Tag className={className}>{children ?? value}</Tag>;
  }

  const handleFocus = () => {
    setEditing(true);
  };
  const handleBlur = async () => {
    const next = ref.current?.innerText ?? '';
    const original = initialRef.current ?? '';
    setEditing(false);
    if (next === original) return;
    setBusy(true);
    const ok = await saveField({ ...scope, value: next });
    setBusy(false);
    if (ok) {
      initialRef.current = next;
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1200);
    } else if (ref.current) {
      // Rollback
      ref.current.innerText = original;
    }
  };
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (ref.current) ref.current.innerText = initialRef.current ?? '';
      ref.current?.blur();
    }
    if (!multiline && e.key === 'Enter') {
      e.preventDefault();
      ref.current?.blur();
    }
  };

  return (
    <Tag
      ref={ref}
      className={`${className} inline-editable ${editing ? 'inline-editable-active' : ''}`}
      contentEditable
      suppressContentEditableWarning
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      spellCheck={false}
      data-inline-scope={`${scope.type}:${scope.sectionId || scope.speakerId || ''}:${scope.path || scope.field || ''}`}
      style={{ position: 'relative' }}
    >
      {children ?? value ?? <span style={{ opacity: 0.4 }}>{placeholder}</span>}
      {busy && (
        <span className="inline-edit-indicator" contentEditable={false}>
          <Loader2 className="w-3 h-3 animate-spin" />
        </span>
      )}
      {savedFlash && !busy && (
        <span className="inline-edit-indicator inline-edit-saved" contentEditable={false}>
          <Check className="w-3 h-3" />
        </span>
      )}
    </Tag>
  );
};
